/**
 * The page-side half of offline support: registration, the download the sidebar
 * button triggers, and the escape hatches.
 *
 * The worker itself is src/sw/service-worker.js; everything here is about giving
 * the user (and whoever has to debug this at 2am) a way to see and undo what it
 * has done.
 */

/**
 * Whether this build ships offline support, as seen from the browser.
 *
 * The build side reads the same variable through offline-config.js, which cannot
 * be imported here: tsconfig only includes ./src, so a root-level .js is outside
 * the program. Both read `GATSBY_OFFLINE`, which webpack inlines into the client
 * bundle precisely because of the GATSBY_ prefix, so the two agree by
 * construction rather than by anyone remembering to keep them in step.
 */
export const OFFLINE_ENABLED = process.env.GATSBY_OFFLINE === "1";

export type OfflineGroup =
  | "core"
  | "pages"
  | "images"
  | "dictionaries"
  | "media"
  | "music";

export type OfflineManifest = {
  groups: Record<OfflineGroup, string[]>;
  sizes: Record<OfflineGroup, number>;
  order: OfflineGroup[];
  fileCount: number;
  totalBytes: number;
  chunkSource: string;
};

export type DownloadProgress = {
  done: number;
  total: number;
  failed: number;
  /** False when the browser declined to exempt this data from eviction. */
  persisted: boolean;
};

/**
 * Groups offered in the sidebar, and whether they are ticked by default.
 *
 * `core` is not listed because it is never optional - a download without the app
 * shell produces a site that cannot start. It is always included.
 */
export const DOWNLOADABLE: Array<{ id: OfflineGroup; defaultOn: boolean }> = [
  { id: "pages", defaultOn: true },
  { id: "images", defaultOn: true },
  { id: "dictionaries", defaultOn: false },
  { id: "media", defaultOn: false },
  { id: "music", defaultOn: false },
];

const isSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "caches" in window;

/**
 * Sends one command to the active worker and resolves when it reports DONE or
 * ERROR, forwarding PROGRESS to `onProgress` along the way.
 *
 * A fresh MessageChannel per command keeps concurrent operations from reading
 * each other's replies, and means a caller that goes away simply stops listening
 * rather than corrupting anyone else's state.
 */
function command<T = any>(
  type: string,
  payload?: unknown,
  onProgress?: (progress: DownloadProgress) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    const controller = navigator.serviceWorker.controller;
    if (!controller) {
      reject(new Error("No active service worker"));
      return;
    }

    const channel = new MessageChannel();
    channel.port1.onmessage = event => {
      const data = event.data || {};
      if (data.type === "PROGRESS") onProgress?.(data);
      else if (data.type === "ERROR") reject(new Error(data.message));
      else if (data.type === "DONE" || data.type === "PONG") resolve(data);
    };

    controller.postMessage({ type, payload }, [channel.port2]);
  });
}

/**
 * Registers the worker.
 *
 * Called from gatsby-browser.js, and only when offline mode is on. Registration
 * failures are swallowed on purpose: offline support is an enhancement, and a
 * site that refuses to work because its cache layer would not install is a worse
 * outcome than one that is merely online-only.
 */
export async function register(): Promise<boolean> {
  if (!isSupported()) return false;

  // The manual escape hatch, before anything else runs. Anyone stuck behind a
  // bad worker can be told to visit mahanaya.org/?sw-reset - no console, no
  // devtools, no "hold shift and click reload" over the phone.
  if (window.location.search.includes("sw-reset")) {
    await unregisterEverything();
    window.location.replace(window.location.pathname);
    return false;
  }

  try {
    await navigator.serviceWorker.register("/sw.js");
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Removes every worker and cache on this origin, whoever created them.
 *
 * Deliberately does not go through the worker's own UNREGISTER command: this has
 * to work when the active worker is broken or unresponsive, which is exactly the
 * situation it exists for.
 */
export async function unregisterEverything(): Promise<void> {
  if (!isSupported()) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map(registration => registration.unregister()));

  const names = await caches.keys();
  await Promise.all(names.map(name => caches.delete(name)));
}

export async function fetchManifest(): Promise<OfflineManifest> {
  const response = await fetch("/offline-manifest.json", { cache: "no-store" });
  if (!response.ok) throw new Error(`Manifest unavailable (${response.status})`);
  return response.json();
}

/**
 * Downloads the chosen groups. `core` is always prepended, since a download
 * without the app shell cannot boot offline.
 */
export async function download(
  groups: OfflineGroup[],
  onProgress?: (progress: DownloadProgress) => void
): Promise<DownloadProgress> {
  const withCore = ["core", ...groups.filter(group => group !== "core")];
  return command<DownloadProgress>("DOWNLOAD", { groups: withCore }, onProgress);
}

/** Drops the downloaded content but leaves the worker installed. */
export async function clearDownload(): Promise<void> {
  await command("CLEAR_DOWNLOAD");
}

/**
 * What is currently stored, for display next to the button.
 *
 * `usage` covers everything this origin holds - Cache Storage plus the
 * IndexedDB the dictionary already uses via idb-cache - which is the honest
 * number to show, because that is what the browser counts against the quota.
 */
export async function status(): Promise<{
  supported: boolean;
  registered: boolean;
  hasDownload: boolean;
  usageBytes: number;
  quotaBytes: number;
  persisted: boolean;
}> {
  if (!isSupported()) {
    return { supported: false, registered: false, hasDownload: false, usageBytes: 0, quotaBytes: 0, persisted: false };
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const cacheNames = await caches.keys();
  // The content layer, which holds both bulk downloads and the pages picked up
  // by ordinary reading. Must stay in step with CONTENT_CACHE in
  // src/sw/service-worker.js; a stale prefix here silently reports "nothing
  // stored" and hides the delete button.
  const contentCache = cacheNames.find(name => name.startsWith("mahanaya-content"));

  let hasDownload = false;
  if (contentCache) {
    const cache = await caches.open(contentCache);
    hasDownload = (await cache.keys()).length > 0;
  }

  const estimate = navigator.storage?.estimate ? await navigator.storage.estimate() : { usage: 0, quota: 0 };
  const persisted = navigator.storage?.persisted ? await navigator.storage.persisted() : false;

  return {
    supported: true,
    registered: !!registration?.active,
    hasDownload,
    usageBytes: estimate.usage || 0,
    quotaBytes: estimate.quota || 0,
    persisted,
  };
}

/** Formats bytes for the sidebar, which has about eight characters to play with. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
